/**
 * Rax Status Bar Component
 *
 * Always-visible bar showing:
 * - Active task (what's running right now)
 * - Session cost (running total)
 * - Action count (this turn)
 * - Next checkpoint (when agent will check in)
 */

import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { raxTheme } from "../styles/rax-theme.js";

export interface StatusBarData {
  /** Currently running task, if any */
  activeTask?: {
    name: string;
    elapsedMs: number;
  };
  /** Total cost for this session */
  sessionCostUsd: number;
  /** Cost for the current turn */
  turnCostUsd: number;
  /** Number of actions this turn */
  turnActionCount: number;
  /** Next scheduled checkpoint (ISO string) */
  nextCheckpoint?: string;
  /** Agent status */
  status: "idle" | "thinking" | "running" | "error";
}

@customElement("rax-status-bar")
export class RaxStatusBar extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
        background: var(--bg-secondary);
        color: var(--text-primary);
        font-size: var(--font-xs);
        padding: var(--space-2) var(--space-4);
        border-top: 1px solid var(--border-color);
      }
      
      .status-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-4);
        flex-wrap: wrap;
      }
      
      .status-section {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .status-icon {
        font-size: var(--font-sm);
      }
      
      .status-label {
        color: var(--text-tertiary);
      }
      
      .status-value {
        font-weight: 500;
      }
      
      .status-divider {
        width: 1px;
        height: 16px;
        background: var(--border-color);
      }
      
      /* Active task animation */
      .active-task {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .spinner {
        width: 12px;
        height: 12px;
        border: 2px solid var(--border-color);
        border-top-color: var(--rax-yellow);
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
      
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
      
      .task-elapsed {
        font-family: monospace;
        color: var(--text-tertiary);
      }
      
      /* Cost highlighting */
      .cost-value {
        font-family: monospace;
        color: var(--rax-yellow);
      }
      
      .cost-value.high {
        color: var(--warning);
      }
      
      .cost-value.very-high {
        color: var(--error);
      }
      
      /* Status indicators */
      .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
      }
      
      .status-dot.idle {
        background: var(--success);
        box-shadow: 0 0 8px var(--success);
      }
      
      .status-dot.thinking {
        background: var(--rax-yellow);
        animation: pulse 1.5s ease-in-out infinite;
        box-shadow: 0 0 8px var(--rax-yellow);
      }
      
      .status-dot.running {
        background: var(--rax-amber);
        animation: pulse 1s ease-in-out infinite;
        box-shadow: 0 0 8px var(--rax-amber);
      }
      
      .status-dot.error {
        background: var(--error);
        box-shadow: 0 0 8px var(--error);
      }
      
      @keyframes pulse {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }
      
      /* Clickable sections */
      .clickable {
        cursor: pointer;
        padding: var(--space-1) var(--space-2);
        border-radius: var(--radius-sm);
        transition: all var(--transition-fast);
      }
      
      .clickable:hover {
        background: var(--bg-hover);
        color: var(--rax-yellow);
      }
      
      /* Responsive */
      @media (max-width: 600px) {
        .status-bar {
          justify-content: center;
        }
      
        .status-divider {
          display: none;
        }
      }
    `,
  ];

  @property({ type: Object })
  data: StatusBarData = {
    sessionCostUsd: 0,
    turnCostUsd: 0,
    turnActionCount: 0,
    status: "idle",
  };

  override render() {
    const { activeTask, sessionCostUsd, turnCostUsd, turnActionCount, nextCheckpoint, status } =
      this.data;

    return html`
      <div class="status-bar">
        <!-- Active Task / Status -->
        <div class="status-section">
          ${
            activeTask
              ? html`
            <div class="active-task">
              <div class="spinner"></div>
              <span class="status-value">${activeTask.name}</span>
              <span class="task-elapsed">(${this.formatDuration(activeTask.elapsedMs)})</span>
            </div>
          `
              : html`
            <div class="status-dot ${status}"></div>
            <span class="status-value">${this.getStatusText(status)}</span>
          `
          }
        </div>

        <div class="status-divider"></div>

        <!-- Turn Cost -->
        <div class="status-section clickable" @click=${this.onTurnCostClick}>
          <span class="status-icon">⚡</span>
          <span class="cost-value ${this.getCostClass(turnCostUsd)}">
            ${this.formatCost(turnCostUsd)}
          </span>
          <span class="status-label">this turn</span>
        </div>

        <div class="status-divider"></div>

        <!-- Action Count -->
        <div class="status-section clickable" @click=${this.onActionsClick}>
          <span class="status-icon">📋</span>
          <span class="status-value">${turnActionCount}</span>
          <span class="status-label">action${turnActionCount !== 1 ? "s" : ""}</span>
        </div>

        <div class="status-divider"></div>

        <!-- Session Total -->
        <div class="status-section clickable" @click=${this.onSessionCostClick}>
          <span class="status-label">Session:</span>
          <span class="cost-value ${this.getCostClass(sessionCostUsd)}">
            ${this.formatCost(sessionCostUsd)}
          </span>
        </div>

        ${
          nextCheckpoint
            ? html`
          <div class="status-divider"></div>
          <div class="status-section">
            <span class="status-icon">⏱️</span>
            <span class="status-value">${this.formatNextCheckpoint(nextCheckpoint)}</span>
          </div>
        `
            : ""
        }
      </div>
    `;
  }

  private getStatusText(status: StatusBarData["status"]): string {
    switch (status) {
      case "idle":
        return "Ready";
      case "thinking":
        return "Thinking...";
      case "running":
        return "Running...";
      case "error":
        return "Error";
    }
  }

  private formatCost(usd: number): string {
    if (usd < 0.01) {
      return `$${usd.toFixed(3)}`;
    }
    return `$${usd.toFixed(2)}`;
  }

  private getCostClass(usd: number): string {
    if (usd >= 1) return "very-high";
    if (usd >= 0.1) return "high";
    return "";
  }

  private formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  private formatNextCheckpoint(iso: string): string {
    const date = new Date(iso);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();

    if (diffMs < 0) return "now";
    if (diffMs < 60000) return `${Math.ceil(diffMs / 1000)}s`;
    if (diffMs < 3600000) return `${Math.ceil(diffMs / 60000)}m`;

    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  private onTurnCostClick() {
    this.dispatchEvent(new CustomEvent("show-turn-details", { bubbles: true }));
  }

  private onActionsClick() {
    this.dispatchEvent(new CustomEvent("show-audit-feed", { bubbles: true }));
  }

  private onSessionCostClick() {
    this.dispatchEvent(new CustomEvent("show-cost-breakdown", { bubbles: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-status-bar": RaxStatusBar;
  }
}
